<template>
    <div class="speakers">
        <div class="speakers-container" id="speakers">
            <div class="title">
                <h3>
                    <em></em>
                    <span>{{ t('speakers.title') }}</span>
                    <em></em>
                </h3>
                <!-- <p style="color: #333;">Continuously updated</p> -->
            </div>
            <div class="speakers-content" v-for="group in groupedSpeakers" :key="group.group_name">
                <div class="speaker-tip" style="text-align: center; font-size: 14px; margin-bottom: 20px;">
                    <p v-if="group.group_name">{{ group.group_name }}</p>
                </div>
                <div class="speaker-list" v-loading="loading" v-if="group.speakers.length > 0">
                    <div v-for="speaker in group.speakers" :key="speaker.id" class="speaker-item"
                        @click="handleSpeakerClick(speaker)">
                        <div class="speaker-avatar">
                            <img :src="getSpeakerAvatar(speaker.avatar)" :alt="getSpeakerName(speaker)"
                                @error="handleImageError" />
                        </div>
                        <div class="speaker-info">
                            <h4 class="speaker-name">{{ getSpeakerName(speaker) }}</h4>
                            <p class="speaker-title">{{ getSpeakerBio(speaker) }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { ref, onMounted, computed } from 'vue'
import { getSpeakers } from '@/api/speaker'
import { previewFile } from '@/utils/uploadedPreview'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'

const { t, locale } = useI18n()
const router = useRouter()

// 响应式数据
const loading = ref(false)
const speakers = ref([])

// 按分组计算演讲者
const groupedSpeakers = computed(() => {
    const groupMap = {}
    speakers.value.forEach(speaker => {
        const groupName = speaker.group_name || ''
        if (!groupMap[groupName]) {
            groupMap[groupName] = {
                group_name: groupName,
                speakers: []
            }
        }
        groupMap[groupName].speakers.push(speaker)
    })
    // 按分组名称排序，空分组（未分组）放在最后
    return Object.values(groupMap).sort((a, b) => {
        if (!a.group_name) return 1
        if (!b.group_name) return -1
        return a.group_name.localeCompare(b.group_name)
    })
})

// 获取演讲者列表
const fetchSpeakers = async () => {
    loading.value = true
    try {
        const response = await getSpeakers({ per_page: 50 })
        if (response.success) {
            // 后端已经按sort_order排序，直接使用返回的数据
            speakers.value = response.data
        } else {
            ElMessage.error(response.message || '获取演讲者列表失败')
        }
    } catch (error) {
        console.error('获取演讲者列表失败:', error)
        ElMessage.error('获取演讲者列表失败')
    } finally {
        loading.value = false
    }
}

// 获取演讲者头像
const getSpeakerAvatar = (avatar) => {
    if (avatar) {
        return previewFile(avatar)
    }
    return '/default-avatar.png' // 默认头像
}

// 获取演讲者姓名
const getSpeakerName = (speaker) => {
    if (locale.value === 'en') {
        // 英文环境：优先显示name_en，不存在时显示name_zh
        return speaker.name_en || speaker.name_zh
    } else {
        // 中文环境：优先显示name_zh，不存在时显示name_en
        return speaker.name_zh || speaker.name_en
    }
}

// 获取演讲者简介
const getSpeakerBio = (speaker) => {
    const bio = locale.value === 'en' ? (speaker.bio_en || speaker.bio_zh || '') : (speaker.bio_zh || speaker.bio_en || '')
    return bio.length > 100 ? bio.substring(0, 100) + '...' : bio
}

// 处理图片加载错误
const handleImageError = (event) => {
    event.target.src = '/default-avatar.png'
}

// 处理演讲者点击
const handleSpeakerClick = (speaker) => {
    router.push(`/speaker/${speaker.id}`)
}

// 组件挂载时获取数据
onMounted(() => {
    fetchSpeakers()
})
</script>


<style lang="scss" scoped>
.speakers {
    background-color: #f3f2f5;
}

.speakers-container {
    padding: 40px 0;
    max-width: 1440px;
    margin: 0 auto;

    .title {
        text-align: center;
        margin-bottom: 40px;
        h3 {
            font-size: 30px;
            font-weight: bold;
            text-align: center;
            color: #303030;
            margin-bottom: 20px;

            span {
                margin: 0 40px;
            }

            em {
                display: inline-block;
                width: 54px;
                height: 14px;
                margin-bottom: 0;
                background: url('@/assets/tt.png');
                vertical-align: middle;
            }
        }
    }

    .speakers-content {
        margin-bottom: 40px;
        border-bottom: 1px solid #e5e5e5;
        padding-bottom: 40px;

        &:last-child {
            margin-bottom: 0;
            border-bottom: none;
            padding-bottom: 0;
        }

        .program-tip {
            margin-bottom: 40px;

            p {
                font-size: 16px;
                color: #303030;
                text-align: center;
            }
        }

        .speaker-list {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 5px;
            padding: 0 20px;

            .speaker-item {
                display: flex;
                align-items: center;
                padding: 10px;
                transition: all 0.3s ease;
                cursor: pointer;

                .speaker-avatar {
                    margin-right: 10px;
                    flex-shrink: 0;

                    img {
                        width: 90px;
                        height: 90px;
                        border-radius: 50%;
                        object-fit: cover;
                    }
                }

                .speaker-info {
                    flex: 1;
                    min-width: 0;

                    .speaker-name {
                        font-size: 20px;
                        font-weight: normal;
                        color: #04425c;
                        margin: 0 0 4px 0;
                        line-height: 1.2;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .speaker-title {
                        font-size: 16px;
                        color: #777;
                        line-height: 1.3;
                        margin: 0;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }
            }
        }
    }

    // 响应式设计
    @media (max-width: 768px) {
        padding: 20px 0;

        .title {
            em {
                width: 40px !important;
            }

            span {
                font-size: 22px;
                margin: 0 10px !important;
            }
        }

        .speakers-content .speaker-list {
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            gap: 20px;
            padding: 0 15px;
        }
    }

    @media (max-width: 480px) {
        .speakers-content .speaker-list {
            grid-template-columns: 1fr;
            gap: 15px;
        }
    }
}
</style>